/**
 * Where a cartoon's files are, and what they are called.
 *
 * A cartoon is one folder — the patched `.riv` with the story artboards, the
 * rendered videos, and the narration / music / sfx / ambience files — named by
 * the `S<major>.<minor>` convention `segments.ts` reads. The folder is NOT in
 * git (87 MB of video that churns on every re-render), so the player cannot
 * bake the file list into the bundle the way the Character Editor does with
 * `import.meta.glob`. Instead `scripts/cartoon-manifest.mjs` writes a small
 * manifest next to the files, and that IS committed.
 *
 * `VITE_CARTOON_BASE` says where the folder is served from: empty (the default)
 * means this origin, i.e. `assets/cartoons/<id>/` in development and whatever
 * was deployed with the site in production. Point it at a CDN prefix and the
 * media never touches the app container.
 */

export interface CartoonManifest {
  id: string;
  /** The Rive file with the story artboards. */
  riv: string;
  videos: { name: string; duration?: number }[];
  audio: { name: string }[];
}

const BASE = ((import.meta.env.VITE_CARTOON_BASE as string | undefined) ?? '').replace(/\/+$/, '');

/** Served URL of one file of a cartoon. */
export const cartoonUrl = (id: string, file: string): string =>
  `${BASE}/cartoons/${encodeURIComponent(id)}/${encodeURIComponent(file)}`;

/** The manifest, or null when this cartoon is not deployed here. */
export async function loadCartoonManifest(id: string): Promise<CartoonManifest | null> {
  try {
    const r = await fetch(cartoonUrl(id, 'manifest.json'), { cache: 'no-cache' });
    if (!r.ok) return null;
    const m = (await r.json()) as CartoonManifest;
    if (!m || typeof m.riv !== 'string' || !Array.isArray(m.videos)) return null;
    return { id, riv: m.riv, videos: m.videos, audio: Array.isArray(m.audio) ? m.audio : [] };
  } catch {
    return null;
  }
}
